import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, BarChart3, Palette, Megaphone } from "lucide-react";
import { useCookieConsent, CookieCategories } from "@/contexts/CookieConsentContext";

export function CookiePreferencesDialog() {
  const { t } = useTranslation();
  const { consent, isPreferencesOpen, closePreferences, updateConsent } = useCookieConsent();

  const [local, setLocal] = useState<CookieCategories>({
    analytics: false,
    personalization: false,
    marketing: false,
  });

  // Sync local state when dialog opens
  useEffect(() => {
    if (isPreferencesOpen && consent) {
      setLocal(consent);
    }
  }, [isPreferencesOpen, consent]);

  const handleSave = () => {
    updateConsent(local);
    closePreferences();
  };

  const categories = [
    {
      key: "necessary" as const,
      icon: Shield,
      label: t("cookies.necessary"),
      description: t("cookies.necessaryDescription"),
      checked: true,
      disabled: true,
    },
    {
      key: "analytics" as const,
      icon: BarChart3,
      label: t("cookies.analytics"),
      description: t("cookies.analyticsDescription"),
      checked: local.analytics,
      disabled: false,
    },
    {
      key: "personalization" as const,
      icon: Palette,
      label: t("cookies.personalization"),
      description: t("cookies.personalizationDescription"),
      checked: local.personalization,
      disabled: false,
    },
    {
      key: "marketing" as const,
      icon: Megaphone,
      label: t("cookies.marketing"),
      description: t("cookies.marketingDescription"),
      checked: local.marketing,
      disabled: false,
    },
  ];

  return (
    <Dialog open={isPreferencesOpen} onOpenChange={(open) => !open && closePreferences()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("cookies.preferencesTitle")}</DialogTitle>
          <DialogDescription>{t("cookies.preferencesDescription")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div key={cat.key} className="flex items-start gap-3">
                <div className="p-1.5 rounded-md bg-primary/10 mt-0.5">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-sm font-medium">{cat.label}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{cat.description}</p>
                </div>
                <Switch
                  checked={cat.checked}
                  disabled={cat.disabled}
                  onCheckedChange={(checked) => {
                    if (cat.key !== "necessary") {
                      setLocal((prev) => ({ ...prev, [cat.key]: checked }));
                    }
                  }}
                />
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button onClick={handleSave} className="w-full sm:w-auto">
            {t("cookies.savePreferences")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
